import {JSX, useEffect, useState} from "react";
import {
    $createParagraphNode,
    $getSelection,
    $isRangeSelection,
    FORMAT_TEXT_COMMAND,
    TextFormatType,
    UNDO_COMMAND,
    REDO_COMMAND,
} from "lexical";
import {$createHeadingNode, HeadingNode} from "@lexical/rich-text";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode} from "@lexical/link";
import {$setBlocksType} from "@lexical/selection";
import {INSERT_DASH_LIST_COMMAND} from "@common/components/forms/rich-text-form-field/plugins/dash-list-plugin";
import {useImageUpload} from "@common/components/forms/rich-text-form-field/hooks/use-image-upload.hook";

export interface ActiveFormats {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    hasLink: boolean;
}

export interface ToolbarActions {
    onFormatChange: (command: TextFormatType) => void;
    onInsertLink: (url: string) => void;
    onRemoveLink: () => void;
    onInsertImage: (file?: File) => void;
    onChangeBlockType: (type: string) => void;
    onResetStyles: () => void;
    onUndo: () => void;
    onRedo: () => void;
    activeFormats: ActiveFormats;
    currentBlockType: string;
    currentLinkUrl: string | null;
}

interface ToolbarPluginProps {
    renderToolbar: (props: ToolbarActions) => JSX.Element;
}

const TEXT_FORMATS: TextFormatType[] = [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "code",
    "subscript",
    "superscript",
    "highlight",
];

const BLOCK_TYPE_HANDLERS: Record<string, (editor: any, selection: any) => void> = {
    heading1: (_, selection) => $setBlocksType(selection, () => $createHeadingNode("h1")),
    heading2: (_, selection) => $setBlocksType(selection, () => $createHeadingNode("h2")),
    paragraph: (_, selection) => $setBlocksType(selection, () => $createParagraphNode()),
    bullet: (editor) => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    numbered: (editor) => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    dash: (editor) => editor.dispatchCommand(INSERT_DASH_LIST_COMMAND, undefined),
    removeList: (editor) => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined),
};

export default function ToolbarPlugin({renderToolbar}: ToolbarPluginProps) {
    const [editor] = useLexicalComposerContext();
    const {handleFileUpload} = useImageUpload();
    const [activeFormats, setActiveFormats] = useState<ActiveFormats>({
        bold: false,
        italic: false,
        underline: false,
        hasLink: false,
    });
    const [blockType, setBlockType] = useState<string>("paragraph");
    const [currentLinkUrl, setCurrentLinkUrl] = useState<string | null>(null);

    const handleFormatChange = (command: TextFormatType): void => {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, command);
    };

    const handleInsertLink = (url: string): void => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    };

    const handleRemoveLink = (): void => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    };

    const handleInsertImage = async (file?: File): Promise<void> => {
        if (!file) return;
        await handleFileUpload(file);
    };

    const handleBlockChange = (type: string): void => {
        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const handler = BLOCK_TYPE_HANDLERS[type];
            if (handler) {
                handler(editor, selection);
            }
        });
    };

    const handleResetStyles = (): void => {
        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            TEXT_FORMATS.forEach((format) => {
                if (selection.hasFormat(format)) {
                    selection.formatText(format);
                }
            });

            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        });
    };

    const handleUndo = (): void => {
        editor.dispatchCommand(UNDO_COMMAND, undefined);
    };

    const handleRedo = (): void => {
        editor.dispatchCommand(REDO_COMMAND, undefined);
    };

    const updateActiveFormats = (selection: any): void => {
        let hasLink = false;
        let linkUrl: string | null = null;

        const nodes = selection.getNodes();
        for (const node of nodes) {
            const parent = node.getParent();
            if ($isLinkNode(node)) {
                hasLink = true;
                linkUrl = (node as LinkNode).getURL();
                break;
            }
            if (parent && $isLinkNode(parent)) {
                hasLink = true;
                linkUrl = (parent as LinkNode).getURL();
                break;
            }
        }

        setActiveFormats({
            bold: selection.hasFormat("bold"),
            italic: selection.hasFormat("italic"),
            underline: selection.hasFormat("underline"),
            hasLink,
        });
        setCurrentLinkUrl(linkUrl);
    };

    const getBlockTypeFromNode = (node: any): string => {
        if ($isListNode(node)) {
            const nodeType = node.getType();
            if (nodeType === "dash-list") return "dash";

            const listType = node.getListType();
            return listType === "number" ? "numbered" : "bullet";
        }

        const nodeType = node.getType();
        if (nodeType === "heading") {
            const heading = node as HeadingNode;
            const tag = heading.getTag();
            return tag === "h1" ? "heading1" : tag === "h2" ? "heading2" : "paragraph";
        }

        return "paragraph";
    };

    useEffect(() => {
        return editor.registerUpdateListener(({editorState}) => {
            editorState.read(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;

                updateActiveFormats(selection);

                const anchorNode = selection.anchor.getNode();
                // Dla niektórych nodów (np. root/decorator) getTopLevelElementOrThrow rzuca błąd – wtedy po prostu pomijamy
                if (anchorNode.getType() === "root") {
                    return;
                }

                let newBlockType = blockType;
                try {
                    const topLevelElement = anchorNode.getTopLevelElementOrThrow();
                    newBlockType = getBlockTypeFromNode(topLevelElement);
                } catch {
                    // ignorujemy przypadki, gdzie nie da się wyznaczyć top-level elementu
                }

                setBlockType(newBlockType);
            });
        });
    }, [editor]);

    return renderToolbar({
        onFormatChange: handleFormatChange,
        onInsertLink: handleInsertLink,
        onRemoveLink: handleRemoveLink,
        onInsertImage: handleInsertImage,
        onChangeBlockType: handleBlockChange,
        onResetStyles: handleResetStyles,
        onUndo: handleUndo,
        onRedo: handleRedo,
        activeFormats,
        currentBlockType: blockType,
        currentLinkUrl,
    });
}
